import { memo, useCallback, useEffect, useState } from 'react';
import { useToggle } from 'react-use';

import { ToggleField } from '@air/component-toggle-field';
import { Button } from '@air/primitive-button';
import { DateInput } from '@air/primitive-date-input';
import { parseAirDateToISO } from '@air/utilities';

import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { useSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/SharePrivateCollectionModalContext';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

export const ExpirationDateSetting = memo(() => {
  const { shortUrlProps, updateExpirationDate, saveExpirationDate, isEditMode, errors, isSaving } =
    useSharePrivateCollectionModal();
  const { expirationDateEnabled } = useShareSettings();
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    shortUrlProps.expirationDate ? parseAirDateToISO(shortUrlProps.expirationDate) : null,
  );
  const [isShowingExpirationDateForm, showExpirationDateForm] = useToggle(!!expirationDate);

  // Sync local state with context changes (e.g., after save)
  useEffect(() => {
    const newDate = shortUrlProps.expirationDate ? parseAirDateToISO(shortUrlProps.expirationDate) : null;
    setExpirationDate(newDate);
    showExpirationDateForm(!!newDate);
  }, [shortUrlProps.expirationDate, showExpirationDateForm]);

  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      if (isShowingExpirationDateForm) {
        // Check if there was a previous value BEFORE updating state
        const hadExpirationDate = shortUrlProps.expirationDate !== null;
        setExpirationDate(null);
        updateExpirationDate(null);
        // In edit mode, only save if there was a previous value (avoid no-op)
        // Pass explicit null to avoid race condition with state update
        if (isEditMode && hadExpirationDate) {
          saveExpirationDate?.(null);
        }
      }
      showExpirationDateForm(checked);
    },
    [
      isShowingExpirationDateForm,
      updateExpirationDate,
      showExpirationDateForm,
      isEditMode,
      saveExpirationDate,
      shortUrlProps.expirationDate,
    ],
  );

  const handleChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) {
        return;
      }

      setExpirationDate(date);
      // Always update parent state immediately for validation feedback
      updateExpirationDate(date?.toISOString() || null);
    },
    [updateExpirationDate],
  );

  const handleSave = useCallback(() => {
    saveExpirationDate?.();
  }, [saveExpirationDate]);

  return (
    <div className="py-2">
      <ToggleField
        label="Add an expiration date"
        id="expiration-date"
        checked={isShowingExpirationDateForm}
        dataTestId="EXPIRATION_DATE_SETTING_TOGGLE"
        labelTestId="EXPIRATION_DATE_SETTING_LABEL"
        isLoading={isSaving}
        disabled={!expirationDateEnabled || isSaving}
        onCheckedChange={handleCheckedChange}
      >
        {!expirationDateEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
      </ToggleField>

      {expirationDateEnabled && isShowingExpirationDateForm && (
        <>
          <div className="relative mt-3 flex items-center">
            <DateInput
              size="large"
              testId="EXPIRATION_DATE_SETTING_INPUT"
              className="w-full rounded-8"
              name="expirationDate"
              selected={expirationDate}
              onChange={handleChange}
            />
            {isEditMode && (
              <Button
                appearance="filled"
                className="ml-3"
                color="grey"
                data-testid="EXPIRATION_DATE_SETTING_SUBMIT_BUTTON"
                disabled={!expirationDate || !!errors?.expirationDate}
                isLoading={isSaving}
                size="large"
                onClick={handleSave}
                type="button"
              >
                Save
              </Button>
            )}
          </div>
          {errors?.expirationDate && <p className="text-12 text-red-9">{errors.expirationDate}</p>}
        </>
      )}
    </div>
  );
});

ExpirationDateSetting.displayName = 'ExpirationDateSetting';
